import { Combobox, ComboboxItem, Input, InputBase, ScrollArea, SelectProps, useCombobox } from "@mantine/core";
import { IconCaretDownFilled, IconX } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import css from "./McSelect.module.css";

type McSelectProps = Omit<SelectProps, "classNames"> & {
  classNames?: Partial<Record<string, string>>;
  dropdownWidth?: React.CSSProperties["width"];
};

function normalizeData(data: SelectProps["data"]): ComboboxItem[] {
  if (data == null) return [];
  return data
    .map((item) => {
      if (typeof item === "string") {
        return { value: item, label: item };
      }
      if ("group" in item) {
        return item.items.map((i) => (typeof i === "string" ? { value: i, label: i } : i));
      }
      return item;
    })
    .flat();
}

export function McSelect({
  data,
  value,
  defaultValue,
  onChange,
  onClear,
  placeholder,
  leftSection,
  classNames,
  disabled,
  searchable = true,
  clearable,
  nothingFoundMessage,
  renderOption,
  dropdownWidth,
  ...rest
}: McSelectProps) {
  const [search, setSearch] = useState<string>("");
  const [hasOpenedOnce, setHasOpenedOnce] = useState<boolean>(false);
  const [internalValue, setInternalValue] = useState<string | null>(defaultValue ?? null);

  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : internalValue;

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearch("");
    },
    onDropdownOpen: (eventSource) => {
      setHasOpenedOnce(true);
      if (eventSource === "keyboard") {
        combobox.selectActiveOption();
      } else {
        combobox.updateSelectedOptionIndex("active");
      }
    },
  });

  const items = useMemo(() => normalizeData(data), [data]);

  const currentItem = useMemo(() => items.find((entry) => entry.value === currentValue) ?? null, [items, currentValue]);

  const showSearch = searchable !== false && items.length > 10;

  const filteredOptions = useMemo(() => {
    if (!showSearch || search === "") return items;
    return items.filter((item) => item.label.toLowerCase().includes(search.toLowerCase()));
  }, [items, search, showSearch]);

  const cn = classNames as Record<string, string> | undefined;

  const options = filteredOptions.map((item) => (
    <Combobox.Option value={item.value} key={item.value} active={item.value === currentValue}>
      {renderOption ? renderOption({ option: item, checked: item.value === currentValue }) : item.label}
    </Combobox.Option>
  ));

  const handleValueChange = (val: string | null) => {
    if (!isControlled) {
      setInternalValue(val);
    }
    const option = items.find((i) => i.value === val) ?? { value: val ?? "", label: val ?? "" };
    onChange?.(val, option);
  };

  const handleClear = () => {
    handleValueChange(null);
    onClear?.();
  };

  const rightSection =
    clearable && currentValue != null ? (
      <IconX
        size={16}
        style={{ cursor: "pointer" }}
        onClick={(e) => {
          e.stopPropagation();
          handleClear();
        }}
      />
    ) : (
      <IconCaretDownFilled size={17} color="var(--mc-icon-default)" />
    );

  return (
    <Combobox
      store={combobox}
      resetSelectionOnOptionHover
      classNames={{
        dropdown: cn?.dropdown,
        option: cn?.option,
        search: cn?.search,
        empty: cn?.empty,
      }}
      onOptionSubmit={(val) => {
        handleValueChange(val === currentValue ? null : val);
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <InputBase
          component="button"
          type="button"
          pointer
          leftSection={leftSection}
          rightSection={rightSection}
          rightSectionPointerEvents={clearable && currentValue != null ? "all" : "none"}
          onClick={() => combobox.toggleDropdown()}
          disabled={disabled}
          classNames={{ input: [css.trigger, cn?.input].filter(Boolean).join(" ") }}
          {...rest}
        >
          {currentItem != null ? currentItem.label : <Input.Placeholder>{placeholder ?? "Velg..."}</Input.Placeholder>}
        </InputBase>
      </Combobox.Target>

      <Combobox.Dropdown style={dropdownWidth ? { minWidth: dropdownWidth } : undefined}>
        {showSearch && (
          <Combobox.Search
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            placeholder="Søk..."
          />
        )}
        <Combobox.Options>
          {hasOpenedOnce && (
            <ScrollArea.Autosize mah="40vh" type="scroll" classNames={{ thumb: cn?.thumb }}>
              {options.length === 0 ? <Combobox.Empty>{nothingFoundMessage ?? "Ingen treff"}</Combobox.Empty> : options}
            </ScrollArea.Autosize>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
